// The customer tab's "Total to collect" (The Engine): per contract Ejar's own balance (open = Ejar's bill if
// closed now - paid; closed = Ejar's remaining amount) + what is NOT on Ejar yet (Salik, Darb, parking, fines). An Invygo
// or company contract's RENT is the accountant's, its fines / Salik / parking are collected like any contract's (owner,
// 12 Oct 2026: "اى عقود ايا كانت هى ايه لما بضيف مخالفات او سالك او باركينج ... تظهر فى اللى المفروض اخده من العميل").
// Pure functions, so the rules are tested apart from the page.

import { isInvygoContract, noMoneyNoteOf, INVYGO_NO_MONEY_NOTE } from '../ContractsManagement/utils/contractRows';
import { LINE_STATES } from '../BranchCharges/branchCharges.logic';

export const DUE_CAP = 10;    // contracts whose balance is asked of Ejar live (the engine's /ejar/due-now limit)

const r2 = (n) => Math.round(n * 100) / 100;
const notReg = (rows) => (rows || []).filter((r) => r.reg_status !== 'registered');

// Invygo customers pay Invygo, never Yelo (owner, 28 Sep 2026): an Invygo contract (branch code D04 / the Invygo
// branch - contractRows isInvygoContract) has no money on Ejar, so its zero is '—', never 'paid'.
const invygoOf = (c) => isInvygoContract({ contractNo: c?.info?.no, pickupBranch: c?.info?.branch });

// An Invygo or company contract (owner, 1 Oct 2026: "عايز يظهر - بس تكون كل حاجه متسجله"): the accountant settles it
// and Ejar shows no payment for it, so paid / remaining / Total to collect / payment status show '—' with this note;
// its Salik / Darb / parking / fines keep showing on the tiles. '' for any other contract (contractRows noMoneyNoteOf).
export const noMoneyOf = (c) => noMoneyNoteOf({ contractNo: c?.info?.no, pickupBranch: c?.info?.branch, customerType: c?.info?.customerType });

// An Invygo contract's RENT is settled by the accountant with Invygo directly (owner, 28 Sep 2026): the page never
// shows a rent figure for it, never counts it in "Total to collect" - its Salik / Darb / parking / fines stay as on
// any contract. The engine's contract_statement.INVYGO_RENT_NOTE says the same words.
export const INVYGO_RENT_NOTE = 'Rent: settled by the accountant (Invygo)';
// a company contract's rent likewise (the engine's contract_statement.COMPANY_RENT_NOTE - owner, 12 Oct 2026)
export const COMPANY_RENT_NOTE = 'Rent: settled by the accountant (company)';
/** The rent note of an Invygo / company due row (dueRowsOf): what is left out of Total to collect. */
export const rentNoteOf = (r) => (r?.invygo ? INVYGO_RENT_NOTE : COMPANY_RENT_NOTE);

// --- Salik / Darb trip states -------------------------------------------------------------------------------------
// ONE state per trip, the same three as Branch charges (branchCharges.logic LINE_STATES, the engine's
// branch_charges._trip_line): "On Ejar" ONLY for what Ejar really holds (owner, 28 Sep 2026: a Darb-only Unlimited
// Salik contract said "all on Ejar" while Ejar held nothing). The reconcile's mark "Unlimited Salik" makes a trip
// "registered" in salik.db so no page asks for it again - but nothing of it is on Ejar.
const RECONCILE_UNLIMITED = 'unlimited salik';
const STATE_COUNT = { on_ejar: 'onEjarN', unlimited: 'unlimitedN', not_on_ejar: 'notOnEjarN' };
export const TRIP_STATES = { on_ejar: LINE_STATES.on_ejar, unlimited: LINE_STATES.unlimited, not_on_ejar: LINE_STATES.not_on_ejar };

// The contract's ONE Unlimited Salik answer: true / false / null (null = unknown, an engine from before the flag).
// c.unlimitedSalik is the engine's rule (the newest Ejar calculation read - the reconcile's own read on the newest
// contract); the reconcile's fresh answer per kind (ejarCheck.toll / ejarCheck.darb - the package covers Darb too) and
// Ejar's due-now answer are the same read, so any of them saying yes is yes (audit MON-10 / MON-17).
export function unlimitedOf(c, due) {
    if (due?.contracts?.[c?.info?.no]?.unlimitedSalik === true || c?.unlimitedSalik === true
        || c?.ejarCheck?.toll?.unlimitedSalik === true || c?.ejarCheck?.darb?.unlimitedSalik === true) return true;
    return c?.unlimitedSalik === false ? false : null;
}

// One trip's state under the contract's answer (branch_charges.build): a trip registered on Ejar stays "on Ejar" (Ejar
// holds that line - an overcharge on an Unlimited contract is never hidden); Yes = the rest is covered; No = a
// reconcile mark is stale and the trip is charged; unknown = the rows' own marks stand.
export function tripState(r, unl) {
    const registered = r?.reg_status === 'registered';
    const marked = registered && String(r?.reg_note || '').trim().toLowerCase() === RECONCILE_UNLIMITED;
    if (registered && !marked) return 'on_ejar';
    if (marked) return unl === false ? 'not_on_ejar' : 'unlimited';
    return unl === true ? 'unlimited' : 'not_on_ejar';
}

const tripsOf = (c) => [...(c?.toll?.rows || []), ...(c?.darb?.rows || [])];

// A contract's Salik + Darb trips counted by state: { n, onEjarN, unlimitedN, notOnEjarN, unlimited }.
export function salikStateOf(c, due) {
    const unl = unlimitedOf(c, due);
    const k = { n: 0, onEjarN: 0, unlimitedN: 0, notOnEjarN: 0, unlimited: unl === true };
    tripsOf(c).forEach((r) => { k.n += 1; k[STATE_COUNT[tripState(r, unl)]] += 1; });
    return k;
}

// A contract's Salik + Darb trips at close (gate + the engine's own trip fee: closeTotal) split by Unlimited Salik:
// { value, covered, charged } - ``charged`` is what the customer pays for them, the Salik card's figure (owner, 26 Oct
// 2026: an Unlimited Salik contract showed its 16 covered trips' 98.70 as its Salik). A trip Ejar really holds stays
// charged (tripState: an overcharge on an Unlimited contract is never hidden).
export function salikValueOf(c, due) {
    const unl = unlimitedOf(c, due);
    const part = (p) => {
        if (!p) return { value: 0, covered: 0 };
        const rows = p.rows || [];
        // the fee the engine put in closeTotal on top of the gates (0 for the company's own staff), never a guess
        const gates = p.total != null ? Number(p.total) || 0 : rows.reduce((a, r) => a + (Number(r.amount) || 0), 0);
        const fee = p.closeTotal != null && rows.length ? (Number(p.closeTotal) - gates) / rows.length : 0;
        const covered = rows.filter((r) => tripState(r, unl) === 'unlimited').reduce((a, r) => a + (Number(r.amount) || 0) + fee, 0);
        return { value: Number(p.closeTotal ?? p.total) || 0, covered };
    };
    const t = part(c?.toll), d = part(c?.darb);
    const value = r2(t.value + d.value), covered = r2(t.covered + d.covered);
    return { value, covered, charged: r2(value - covered) };
}

// The words after "Salik X" (the contract badge, the Excel's "Salik on Ejar?"): "all on Ejar" ONLY when Ejar holds
// every trip; never "on Ejar" for a covered or missing one.
export function salikWordsOf(k) {
    if (!k || !k.n) return k?.unlimited ? 'Unlimited Salik (no trips)' : '—';
    if (k.onEjarN === k.n) return 'all on Ejar';
    if (k.unlimitedN === k.n) return 'covered by Unlimited Salik';
    return [
        k.notOnEjarN && `${k.notOnEjarN} trip(s) not on Ejar`,
        k.onEjarN && `${k.onEjarN} on Ejar`,
        k.unlimitedN && `${k.unlimitedN} Unlimited Salik`,
    ].filter(Boolean).join(' · ');
}

// The badge's colour, the state's colour on Branch charges: amber while a trip is still to charge, purple (accent)
// when covered by Unlimited Salik, grey otherwise.
export const salikVariantOf = (k) => (k?.notOnEjarN ? 'warning' : k?.unlimitedN || (k?.unlimited && !k?.n) ? TRIP_STATES.unlimited.variant : 'neutral');

// What one contract owes that Ejar does not carry yet: Salik and Darb = the contract's Salik total when the engine has
// it (salikTotalOf), else the trips whose state is "not on Ejar" + what short "Salik till" lines miss (covered ones owe
// nothing - the package covers every Salik AND Darb gate), parking = its rows not on Ejar,
// fines = the engine's one rule - for every contract, not only the ones Ejar was asked about (audit MON-10 / MON-17).
// A "Salik till" line typed on Ejar at another amount than its trips (the engine's check, owner 1 Oct 2026): the lines
// whose difference is more than rounding - [{ line, till, trips, expected, ejar, diff }], short when diff < 0.
export const tillMismatchesOf = (c) => (c?.tillChecks || []).filter((k) => Math.abs(Number(k.diff) || 0) > 0.05);

// The contract's Salik TOTAL (the engine's branch_charges.salik_checks, owner 1 Oct 2026: "سالك ايا كان تاريخه 50
// والصح يكون 70 يبقى فى 20 مش متضافة"): everything Ejar holds for tolls - every Salik / Darb / Tolls line whatever its
// date, and the Tolls item - against every Salik and Darb trip at gate + 1.05. { ejar, expected, trips, darbTrips, diff,
// short } or null (Unlimited Salik, never read on Ejar, a search cut to some dates).
export const salikTotalOf = (c, due) => (unlimitedOf(c, due) === true ? null : c?.salikTotal || null);

/** The total in words: "20.00 not added" / "3.15 short - below the AED 5 limit, not registered" (grey, owner 8 Oct
 * 2026) / "overcharged 4.85" / "matches" (the engine's salik_total_note). */
export function salikTotalWords(t, fmt = (n) => Number(n).toFixed(2)) {
    if (!t) return '';
    if (Number(t.short) > 0) return `${fmt(t.short)} not added`;
    if (Number(t.belowLimit) > 0) return `${fmt(t.belowLimit)} short - below the AED 5 limit, not registered`;
    // more on Ejar over a SOLD car's period with no trip of ours: no source, not an overcharge (owner, 11 Oct 2026)
    if (Number(t.diff) > 0.05 && t.noSource) return `Ejar holds ${fmt(t.diff)} more - car sold, no Salik data`;
    return Number(t.diff) > 0.05 ? `overcharged ${fmt(t.diff)}` : 'matches';
}

/** The tone of the total's tile: red for money to add, grey for a gap below the limit or no source, amber for an
 * overcharge. */
export const salikTotalTone = (t) => (!t ? 'neutral' : Number(t.short) > 0 ? 'bad' : Number(t.belowLimit) > 0 ? 'neutral'
    : Number(t.diff) > 0.05 && t.noSource ? 'neutral' : Math.abs(Number(t.diff) || 0) > 0.05 ? 'warn' : 'good');

/** A sold car's periods on the contract with nothing of ours for ``kind`` (toll / darb / parking) - the engine's
 * coverage.soldNoData: "car sold - no parking data (P 10374)", else '' (owner, 11 Oct 2026). */
export function soldNoDataNote(c, kind, what) {
    const gone = c?.coverage?.[kind]?.soldNoData || [];
    return gone.length ? `car sold - no ${what} data (${[...new Set(gone.map((w) => w.plate))].join(', ')})` : '';
}

/** What a Salik / Darb trip of the contract carries on top of its gate: the engine's ``tripFee`` (0 for the company's
 * own staff - owner, 1 Oct 2026), else ``fallback`` (TRIP_SURCHARGE). */
export const tripFeeOf = (c, fallback) => (c && c.tripFee != null && Number.isFinite(Number(c.tripFee)) ? Number(c.tripFee) : fallback);

export function notOnEjarOf(c, due, tripSurcharge) {
    const unl = unlimitedOf(c, due);
    // a staff contract's trips carry no AED 1 + VAT: the engine's own figure for the contract (owner, 1 Oct 2026)
    const fee = tripFeeOf(c, tripSurcharge);
    const owed = (rows) => (rows || []).filter((r) => tripState(r, unl) === 'not_on_ejar')
        .reduce((a, r) => a + (Number(r.amount) || 0) + fee, 0);
    const park = notReg(c?.parking?.rows).reduce((a, r) => a + (Number(r.amount) || 0), 0);
    const fines = (c?.fines?.rows || []).reduce((a, r) => a + (Number(r.owed_not_on_ejar) || 0), 0);
    // the total is THE Salik figure when the engine has it: what is not added, Darb inside it - the trips and lines
    // behind it owe nothing apart (counted once)
    const total = salikTotalOf(c, due);
    if (total) return { salik: Number(total.short) || 0, darb: 0, park, fines };
    // what a short "Salik till" line misses is still to enter on Ejar; an overcharged one owes nothing
    const tillShort = tillMismatchesOf(c).reduce((a, k) => a + Math.max(0, -(Number(k.diff) || 0)), 0);
    // fines: the engine's one rule - the fine + its impound, less what Ejar holds (a mismatch owes the shortfall)
    return { salik: owed(c?.toll?.rows) + tillShort, darb: owed(c?.darb?.rows), park, fines };
}

// One row per contract. Ejar's balance is read for the newest ``cap`` contracts only; what is not on Ejar is
// added for EVERY contract, so the total and the "+ not on Ejar" lines under it agree (audit MON-17). No balance
// = no figure: an open contract Ejar could not price "if closed now" is never counted at its whole booking (MON-05).
// An Invygo contract carries no rent (INVYGO_RENT_NOTE): its balance is the engine's "invygo" answer only (the fines
// and charges on Ejar less paid - contract_statement.invygo_due_now); any other answer for it prices its rent, so it
// is never counted - ``invygo`` marks the row.
export function dueRowsOf(cs, due, tripSurcharge, cap = DUE_CAP) {
    return (cs || []).map((c, i) => {
        const d = i < cap ? (due?.contracts?.[c.info.no] || null) : null;
        const invygo = d?.source === 'invygo' || invygoOf(c);
        const noMoney = noMoneyOf(c) || (invygo ? INVYGO_NO_MONEY_NOTE : '');
        const n = notOnEjarOf(c, due, tripSurcharge);
        const extra = n.salik + n.darb + n.park + n.fines;
        // Invygo / company: the rent is the accountant's, the charges are collected (owner, 12 Oct 2026). The engine's
        // "invygo" / "company" answer is exactly that - the lines on Ejar (+ a company's Tolls item) less paid; any other
        // answer for it prices its rent, so it is never counted
        if (noMoney) {
            const chargesOnly = !!d && (d.source === 'invygo' || d.source === 'company');
            const bal = chargesOnly && d.balance != null ? Number(d.balance) : null;
            return { cn: c.info.no, d, n, extra, bal, invygo, noMoney, chargesOnly, capped: i >= cap, total: r2((bal || 0) + extra) };
        }
        // an open contract Ejar could not price "if returned now": the engine's estimate from Ejar's own calculation
        // (its daily items x the days started + the rest - paid), shown as "≈" (owner, 12 Oct 2026)
        const est = d && d.balance == null && d.estimate && d.estimate.balance != null ? d.estimate : null;
        const bal = d && d.balance != null ? Number(d.balance) : (est ? Number(est.balance) : null);
        return { cn: c.info.no, d, n, extra, bal, invygo, noMoney: '', estimated: !!est, capped: i >= cap, total: r2((bal || 0) + extra) };
    });
}

export const dueTotal = (rows) => r2((rows || []).reduce((a, r) => a + r.total, 0));

// A row whose missing balance is a real gap ("Ejar gave no figure"). An Invygo contract the engine did not answer as
// one (it priced it with its rent) is a gap too: its rent is left out on purpose (INVYGO_RENT_NOTE), but so are the
// fines and charges Ejar holds for it, so the page says so instead of a silent, smaller total (review 28 Sep 2026;
// the engine's rule is now the web's - due_now.is_invygo_contract - so this is only a stale engine or a branch-only
// match).
// An Invygo / company row is a gap only when the engine's charges answer for it failed (its rent is never a figure).
export const noFigureOf = (r) => !!(r && r.d && r.bal == null && (!r.noMoney || r.chargesOnly));

// Is this contract's rent the accountant's (INVYGO_RENT_NOTE)? The engine's "invygo" answer, or the web's own
// isInvygoContract - the same rule as dueRowsOf.
export const invygoRentOf = (c, d) => d?.source === 'invygo' || invygoOf(c);

// The Excel Summary's "Ejar remaining" + "Payment status" of an Invygo or company contract: no remaining, the note as
// its status (owner, 1 Oct 2026). null for any other contract.
export const noMoneySummaryOf = (c) => {
    const note = noMoneyOf(c);
    return note ? { remaining: null, status: note } : null;
};

// The customer statement's Payment status (the Excel's Summary sheet): '—' for an Invygo or company contract, whatever
// Ejar says (owner, 1 Oct 2026). Any other contract keeps its status.
export const payStatusOf = (c, status) => (noMoneyOf(c) ? '—' : status);

// Ejar's paid amount as the page and the Excel show it: '—' for an Invygo contract's 0 (the engine reads Ejar's
// empty paid as 0 - due_now.settle); a real amount stays.
export const ejarPaidOf = (c, paid) => (noMoneyOf(c) ? '—' : paid);       // Invygo / company: '—' (owner, 1 Oct 2026)
